// src/shared/composables/useE2EEncryption.ts
//
// End-to-end encryption composable using native WebCrypto only.
//
// Algorithm: PBKDF2-SHA256 (600,000 iterations) → AES-GCM-256.
// No external crypto libraries. Auditable.
//
// The server NEVER sees plaintext, passphrase, key, or decoded filename.
// Only the ciphertext + iv + salt + a public size hint are transmitted.

const PBKDF2_ITERATIONS = 600_000
const SALT_BYTES = 16
const IV_BYTES = 12
const KEY_BITS = 256

export interface EncryptedBlob {
  ivB64: string
  saltB64: string
  ciphertextB64: string
}

export interface EncryptedFilePayload extends EncryptedBlob {
  /** Public, unencrypted size hint of the ciphertext in bytes. */
  sizeBytes: number
  /** Public, unencrypted MIME hint (optional, for receiver UI icon only). */
  mimeTypeHint?: string
}

export interface PlainFileMetadata {
  filename: string
  mimeType: string
  uploadedAt: number
}

/**
 * Generates a cryptographically strong random passphrase suitable for
 * fragment-of-URL transport. Default 32 chars from URL-safe alphabet.
 */
export function generatePassphrase(length = 32): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789'
  const bytes = new Uint8Array(length)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('')
}

function toB64(bytes: ArrayBuffer | Uint8Array): string {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)
  let binary = ''
  for (let i = 0; i < arr.byteLength; i++) binary += String.fromCharCode(arr[i])
  return btoa(binary)
}

function fromB64(b64: string): Uint8Array {
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

async function deriveKey(passphrase: string, salt: Uint8Array): Promise<CryptoKey> {
  const enc = new TextEncoder()
  const baseKey = await crypto.subtle.importKey(
    'raw',
    enc.encode(passphrase),
    { name: 'PBKDF2' },
    false,
    ['deriveKey'],
  )
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt as BufferSource,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    baseKey,
    { name: 'AES-GCM', length: KEY_BITS },
    false,
    ['encrypt', 'decrypt'],
  )
}

/**
 * Encrypts a File together with its metadata (filename, mime) under a
 * passphrase. Output is a structured payload ready to POST to the server.
 *
 * Layout of the plaintext that gets encrypted:
 *   [4 bytes BE: metadata JSON length] [metadata JSON UTF-8] [file bytes]
 *
 * This means filename and MIME are protected by the same AES-GCM tag as the
 * file contents — tampering by the server is detected at decrypt time.
 */
export async function encryptFile(
  file: File,
  passphrase: string,
): Promise<EncryptedFilePayload> {
  const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES))
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES))
  const key = await deriveKey(passphrase, salt)

  const metadata: PlainFileMetadata = {
    filename: file.name,
    mimeType: file.type || 'application/octet-stream',
    uploadedAt: Date.now(),
  }
  const metaBytes = new TextEncoder().encode(JSON.stringify(metadata))
  const metaLen = new Uint8Array(4)
  new DataView(metaLen.buffer).setUint32(0, metaBytes.byteLength, false)

  const fileBytes = new Uint8Array(await file.arrayBuffer())
  const plaintext = new Uint8Array(4 + metaBytes.byteLength + fileBytes.byteLength)
  plaintext.set(metaLen, 0)
  plaintext.set(metaBytes, 4)
  plaintext.set(fileBytes, 4 + metaBytes.byteLength)

  const ciphertextBuf = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    key,
    plaintext as BufferSource,
  )
  const ciphertext = new Uint8Array(ciphertextBuf)

  return {
    ivB64: toB64(iv),
    saltB64: toB64(salt),
    ciphertextB64: toB64(ciphertext),
    sizeBytes: ciphertext.byteLength,
    mimeTypeHint: undefined, // we DON'T leak the real MIME to the server
  }
}

export interface DecryptedFile {
  bytes: Uint8Array
  metadata: PlainFileMetadata
}

/**
 * Decrypts the structured payload produced by encryptFile back into a Uint8Array
 * plus the recovered metadata. Throws on wrong passphrase / tampered ciphertext
 * (DOMException of name 'OperationError' under the hood).
 */
export async function decryptFile(
  payload: EncryptedBlob,
  passphrase: string,
): Promise<DecryptedFile> {
  const salt = fromB64(payload.saltB64)
  const iv = fromB64(payload.ivB64)
  const ciphertext = fromB64(payload.ciphertextB64)
  const key = await deriveKey(passphrase, salt)

  const plainBuf = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    key,
    ciphertext as BufferSource,
  )
  const plain = new Uint8Array(plainBuf)

  const metaLen = new DataView(plain.buffer, plain.byteOffset, 4).getUint32(0, false)
  const metaBytes = plain.slice(4, 4 + metaLen)
  const fileBytes = plain.slice(4 + metaLen)

  const metadata = JSON.parse(new TextDecoder().decode(metaBytes)) as PlainFileMetadata
  return { bytes: fileBytes, metadata }
}

export function useE2EEncryption() {
  return {
    encryptFile,
    decryptFile,
    generatePassphrase,
  }
}

// ---------------------------------------------------------------------------
// Cifrado chunked (Fase 5)
// Divide el plaintext [metalen|meta|file] en chunks de CHUNK_SIZE bytes,
// cada uno cifrado con AES-GCM independiente. Clave maestra única (PBKDF2).
// Nonce por chunk: baseNonce(8B) || uint32_BE(index).
// AAD por chunk: uint32_BE(index) || uint32_BE(total) || is_last(1B).
// ---------------------------------------------------------------------------

export const CHUNK_SIZE = 4 * 1024 * 1024 // 4 MiB de plaintext por chunk

export interface ChunkedEncryptedPayload {
  saltB64: string
  baseNonceB64: string
  chunkSize: number
  totalChunks: number
  totalSize: number // bytes del plaintext completo (metadata + fichero)
  chunksB64: string[]
}

function chunkNonce(baseNonce: Uint8Array, index: number): Uint8Array {
  const nonce = new Uint8Array(IV_BYTES) // 12 bytes
  nonce.set(baseNonce, 0) // 8 bytes base
  new DataView(nonce.buffer).setUint32(8, index, false) // 4 bytes contador BE
  return nonce
}

function chunkAad(index: number, total: number): Uint8Array {
  const aad = new Uint8Array(9)
  const dv = new DataView(aad.buffer)
  dv.setUint32(0, index, false)
  dv.setUint32(4, total, false)
  aad[8] = index === total - 1 ? 1 : 0
  return aad
}

export async function encryptFileChunked(
  file: File,
  passphrase: string,
): Promise<ChunkedEncryptedPayload> {
  const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES))
  const baseNonce = crypto.getRandomValues(new Uint8Array(8))
  const key = await deriveKey(passphrase, salt)

  const metadata: PlainFileMetadata = {
    filename: file.name,
    mimeType: file.type || 'application/octet-stream',
    uploadedAt: Date.now(),
  }
  const metaBytes = new TextEncoder().encode(JSON.stringify(metadata))
  const metaLen = new Uint8Array(4)
  new DataView(metaLen.buffer).setUint32(0, metaBytes.byteLength, false)
  const fileBytes = new Uint8Array(await file.arrayBuffer())

  const plaintext = new Uint8Array(4 + metaBytes.byteLength + fileBytes.byteLength)
  plaintext.set(metaLen, 0)
  plaintext.set(metaBytes, 4)
  plaintext.set(fileBytes, 4 + metaBytes.byteLength)

  const totalChunks = Math.max(1, Math.ceil(plaintext.byteLength / CHUNK_SIZE))
  const chunksB64: string[] = []
  for (let i = 0; i < totalChunks; i++) {
    const slice = plaintext.subarray(
      i * CHUNK_SIZE,
      Math.min((i + 1) * CHUNK_SIZE, plaintext.byteLength),
    )
    const ct = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: chunkNonce(baseNonce, i) as BufferSource,
        additionalData: chunkAad(i, totalChunks) as BufferSource,
      },
      key,
      slice as BufferSource,
    )
    chunksB64.push(toB64(new Uint8Array(ct)))
  }

  return {
    saltB64: toB64(salt),
    baseNonceB64: toB64(baseNonce),
    chunkSize: CHUNK_SIZE,
    totalChunks,
    totalSize: plaintext.byteLength,
    chunksB64,
  }
}

export async function decryptFileChunked(
  manifest: { saltB64: string; baseNonceB64: string; totalChunks: number },
  chunksB64: string[],
  passphrase: string,
): Promise<{ bytes: Uint8Array; metadata: PlainFileMetadata }> {
  const total = manifest.totalChunks
  if (!Number.isInteger(total) || total <= 0) {
    throw new Error(`invalid totalChunks: ${total}`)
  }
  if (chunksB64.length !== total) {
    throw new Error(`chunk count mismatch: got ${chunksB64.length}, expected ${total}`)
  }
  const key = await deriveKey(passphrase, fromB64(manifest.saltB64))
  const baseNonce = fromB64(manifest.baseNonceB64)
  const parts: Uint8Array[] = []
  for (let i = 0; i < total; i++) {
    const pt = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: chunkNonce(baseNonce, i) as BufferSource,
        additionalData: chunkAad(i, total) as BufferSource,
      },
      key,
      fromB64(chunksB64[i]) as BufferSource,
    )
    parts.push(new Uint8Array(pt))
  }
  const totalLen = parts.reduce((n, p) => n + p.byteLength, 0)
  const plain = new Uint8Array(totalLen)
  let off = 0
  for (const p of parts) {
    plain.set(p, off)
    off += p.byteLength
  }

  const metaLen = new DataView(plain.buffer, plain.byteOffset, 4).getUint32(0, false)
  const metaBytes = plain.slice(4, 4 + metaLen)
  const fileBytes = plain.slice(4 + metaLen)
  const metadata = JSON.parse(new TextDecoder().decode(metaBytes)) as PlainFileMetadata
  return { bytes: fileBytes, metadata }
}
